import { useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";
import { toast } from "sonner";

/**
 * VoiceSearchButton — Web Speech API mic button that appends transcribed
 * speech into any text input. Uses `webkitSpeechRecognition` / `SpeechRecognition`.
 *
 * Props:
 *  - onTranscript(text): called with final transcript when the user stops speaking
 *  - lang: "en-US" | "en-GB" (default en-GB — closer to Grenadian English)
 *  - className: extra classes for positioning
 */
export default function VoiceSearchButton({ onTranscript, lang = "en-GB", className = "" }) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const recRef = useRef(null);

  useEffect(() => {
    const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) { setSupported(false); return; }
    const rec = new SR();
    rec.lang = lang;
    rec.continuous = false;
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      const t = e.results[0]?.[0]?.transcript?.trim();
      if (t) onTranscript(t);
    };
    rec.onend = () => setListening(false);
    rec.onerror = (e) => {
      setListening(false);
      const msg = e.error === "not-allowed" ? "Mic permission blocked — allow it in browser settings."
                : e.error === "no-speech"    ? "Didn't catch that — try again."
                : `Voice error: ${e.error || "unknown"}`;
      toast.error(msg);
    };
    recRef.current = rec;
    return () => { try { rec.abort(); } catch { /* noop */ } };
  }, [lang, onTranscript]);

  const toggle = () => {
    if (!recRef.current) return;
    if (listening) {
      try { recRef.current.stop(); } catch { /* noop */ }
      setListening(false);
      return;
    }
    try {
      recRef.current.start();
      setListening(true);
    } catch (e) {
      // start() throws if already started
    }
  };

  if (!supported) return null;

  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="voice-search-btn"
      aria-label={listening ? "Stop listening" : "Start voice search"}
      title={listening ? "Listening…" : "Voice search"}
      className={`relative w-10 h-10 rounded-full flex items-center justify-center transition ${
        listening
          ? "bg-eatup-green text-white shadow-lg"
          : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
      } ${className}`}
    >
      {listening ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
      {listening && (
        <>
          <span className="absolute inset-0 rounded-full bg-eatup-green/40 animate-ping" />
          <span className="absolute -top-1 -right-1 text-[9px] font-black uppercase bg-eatup-yellow text-eatup-charcoal px-1.5 rounded-full">LIVE</span>
        </>
      )}
    </button>
  );
}
